import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

function ClassroomView({ orgId }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [pin, setPin] = useState('');
  const [verified, setVerified] = useState(false);
  const [waitingKids, setWaitingKids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const handleVerifyPin = async () => {
    setError('');
    if (!pin || pin.length !== 4) {
      setError('Please enter a 4 digit PIN.');
      return;
    }

    setLoading(true);

    console.log('Org ID being used:', orgId);

    const { data, error } = await supabase
      .from('organizations')
      .select('classroom_pin')
      .eq('id', orgId)
      .single();

    console.log('Data:', data);
    console.log('Error:', error);

    if (error || !data) {
      setError(t.errorVerifyingPin);
      setLoading(false);
      return;
    }

    console.log('DB PIN:', data.classroom_pin);
console.log('Entered PIN:', pin);

if (data.classroom_pin !== pin) {
      setError(t.incorrectPinTry);
      setLoading(false);
      return;
    }

    setVerified(true);
    setLoading(false);
  };

  const fetchWaitingKids = async () => {
    if (!orgId) return;

    const { data: checkins, error: checkinError } = await supabase
      .from('checkins')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', 'parent_waiting')
      .order('parent_waiting_at', { ascending: true });

    if (checkinError) return;

    if (!checkins.length) {
      setWaitingKids([]);
      setLastUpdated(new Date());
      return;
    }

    const childIds = checkins.map((c) => c.child_id);
    const { data: children } = await supabase
      .from('children')
      .select('*')
      .in('id', childIds);

    const results = checkins.map((checkin) => {
      const child = children.find((c) => c.id === checkin.child_id);
      return {
        ...child,
        checkin_id: checkin.id,
        parent_waiting_at: checkin.parent_waiting_at,
      };
    });

    setWaitingKids(results);
    setLastUpdated(new Date());
  };

  const handleRelease = async (checkinId, childName) => {
    const { error } = await supabase
      .from('checkins')
      .update({ status: 'checked_out', checked_out_at: new Date() })
      .eq('id', checkinId);

    if (error) {
      alert('Error releasing child: ' + error.message);
      return;
    }

    setWaitingKids(waitingKids.filter((k) => k.checkin_id !== checkinId));
  };

  useEffect(() => {
    if (verified) {
      fetchWaitingKids();
      const interval = setInterval(fetchWaitingKids, 10000);
      return () => clearInterval(interval);
    }
  }, [verified]);

  // PIN Entry Screen
  if (!verified) {
    return (
      <div style={pinScreenStyle}>
        <div style={pinCardStyle}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🏫</div>
          <h1 style={{ fontSize: '32px', fontWeight: '900', color: '#1a1a2e', marginBottom: '8px' }}>
            {t.classroomViewTitle}
          </h1>
          <p style={{ color: '#888', marginBottom: '32px', fontSize: '16px' }}>
            {t.enterPinAccess}
          </p>

          <input
            style={pinInputStyle}
            placeholder={t.enter4DigitPin}
            value={pin}
            onChange={(e) => setPin(e.target.value.slice(0, 4))}
            type="password"
            maxLength={4}
          />

          <button style={pinButtonStyle} onClick={handleVerifyPin} disabled={loading}>
            {loading ? t.verifying : t.enterClassroomView}
          </button>

          {error && (
            <div style={errorStyle}>{error}</div>
          )}
        </div>
      </div>
    );
  }

  // Classroom Display Screen
  return (
    <div style={classroomStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '36px' }}>🛡️</span>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '900', color: 'white', margin: 0 }}>
              SafeRoster
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '14px' }}>
              Classroom View
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={countBadgeStyle}>
            {waitingKids.length} {t.waiting}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
            Updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={contentStyle}>
        {waitingKids.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={{ fontSize: '80px', marginBottom: '24px' }}>✅</div>
            <h2 style={{ fontSize: '36px', fontWeight: '900', color: '#1a1a2e', marginBottom: '12px' }}>
              {t.allClear}
            </h2>
            <p style={{ fontSize: '20px', color: '#888' }}>
              {t.noParentsWaiting}
            </p>
          </div>
        ) : (
          <div style={{ width: '100%', maxWidth: '1400px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1a1a2e', marginBottom: '20px', textAlign: 'center' }}>
              {t.parentsWaitingPickup}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: waitingKids.length <= 2
                ? 'repeat(auto-fill, minmax(280px, 1fr))'
                : waitingKids.length <= 6
                ? 'repeat(auto-fill, minmax(220px, 1fr))'
                : 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: waitingKids.length > 6 ? '12px' : '20px',
              width: '100%',
            }}>
              {waitingKids.map((kid) => (
                <div key={kid.checkin_id} style={{
                  ...kidCardStyle,
                  padding: waitingKids.length > 6 ? '16px 12px' : '24px 20px',
                }}>
                  <div style={{ fontSize: waitingKids.length > 6 ? '28px' : '40px', marginBottom: '8px' }}>👶</div>
                  <h2 style={{ fontSize: waitingKids.length > 6 ? '16px' : '22px', fontWeight: '900', color: '#1a1a2e', marginBottom: '6px' }}>
                    {kid.full_name}
                  </h2>
                  <p style={{ color: '#666', fontSize: '13px', marginBottom: '2px' }}>
                    {kid.parent_name}
                  </p>
                  <p style={{ color: '#ff9800', fontSize: '12px', fontWeight: '600', marginBottom: '12px' }}>
                    {t.waitingSinceTime} {new Date(kid.parent_waiting_at).toLocaleTimeString()}
                  </p>
                  <button
                    style={{ ...releaseButtonStyle, padding: waitingKids.length > 6 ? '10px' : '12px', fontSize: '14px' }}
                    onClick={() => handleRelease(kid.checkin_id, kid.full_name)}
                  >
                    {t.release}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: 0 }}>
          {t.autoRefresh}
        </p>
        <button style={exitButtonStyle} onClick={() => setVerified(false)}>
          {t.exit}
        </button>
      </div>
    </div>
  );
}

// PIN Screen Styles
const pinScreenStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #f0f7ff 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const pinCardStyle = {
  background: 'white',
  borderRadius: '24px',
  padding: '48px 40px',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(26,86,219,0.12)',
  border: '1px solid #e8f0fe',
  maxWidth: '400px',
  width: '100%',
};

const pinInputStyle = {
  display: 'block',
  width: '100%',
  padding: '16px',
  fontSize: '24px',
  textAlign: 'center',
  letterSpacing: '8px',
  borderRadius: '12px',
  border: '2px solid #e8f0fe',
  outline: 'none',
  marginBottom: '16px',
  fontFamily: 'inherit',
};

const pinButtonStyle = {
  width: '100%',
  padding: '16px',
  fontSize: '17px',
  fontWeight: '700',
  background: 'linear-gradient(135deg, #1a56db, #1a3a8f)',
  color: 'white',
  border: 'none',
  borderRadius: '50px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const errorStyle = {
  background: '#fdecea',
  color: '#c0392b',
  borderRadius: '12px',
  padding: '14px',
  marginTop: '16px',
  fontWeight: '600',
};

// Classroom Display Styles
const classroomStyle = {
  minHeight: '100vh',
  background: '#f0f4ff',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle = {
  background: 'linear-gradient(135deg, #1a56db, #1a3a8f)',
  padding: '20px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const countBadgeStyle = {
  background: 'rgba(255,255,255,0.2)',
  color: 'white',
  padding: '8px 20px',
  borderRadius: '50px',
  fontSize: '18px',
  fontWeight: '700',
};

const contentStyle = {
  flex: 1,
  padding: '24px 32px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  overflowY: 'auto',
};

const emptyStateStyle = {
  textAlign: 'center',
  padding: '60px 20px',
};


const kidCardStyle = {
  background: 'white',
  borderRadius: '24px',
  padding: '32px 24px',
  textAlign: 'center',
  boxShadow: '0 8px 32px rgba(255,152,0,0.15)',
  border: '3px solid #ff9800',
};

const releaseButtonStyle = {
  width: '100%',
  padding: '14px',
  fontSize: '16px',
  fontWeight: '700',
  background: 'linear-gradient(135deg, #43e97b, #38f9d7)',
  color: 'white',
  border: 'none',
  borderRadius: '50px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const footerStyle = {
  background: 'linear-gradient(135deg, #1a56db, #1a3a8f)',
  padding: '16px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const exitButtonStyle = {
  background: 'rgba(255,255,255,0.2)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: '14px',
  fontFamily: 'inherit',
};

export default ClassroomView;